import { useMutation } from '@apollo/client';
import { SendOrganisationWebhookTestEventDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { SendTestEventResult } from './useSendTestEventTRPC';
import { useSendTestEventTRPC } from './useSendTestEventTRPC';

/**
 * Flag-gated wrapper: tRPC `webhook.sendTestEvent`, or the v2
 * `sendOrganisationWebhookTestEventApi` action on the legacy path.
 */
export const useSendTestEvent = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [sendTestEventGraphQL, graphqlState] = useMutation(
    SendOrganisationWebhookTestEventDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_webhook_delivery');
        evictField(cache, 'organisation_webhook');
      },
    }
  );

  const trpcMutation = useSendTestEventTRPC();

  const sendTestEvent = async (id: string): Promise<SendTestEventResult> => {
    if (trpcEnabled) {
      return trpcMutation.sendTestEvent(id);
    }

    const result = await sendTestEventGraphQL({
      variables: { object: { Id: id } },
    });
    const success = result.data?.sendOrganisationWebhookTestEventApi?.success;
    if (success === undefined) {
      throw new Error('Failed to send test event');
    }

    return { status: success ? 'success' : 'failed' };
  };

  if (trpcEnabled) {
    return {
      sendTestEvent,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    sendTestEvent,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
